/**
 * Composable per Orders With Requirement autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { OrdersWithRequirementOptions } from '@/lib/autocompletes';
import { getOrdersWithRequirementAutocomplete } from '@/lib/autocompletes/ordersWithRequirement';
import { JobOrderAutocompleteResult } from '~/lib/models/jobOrder';

export function useOrdersWithRequirementAutocomplete(
	options: BaseAutocompleteComposableOptions = {},
) {
	const ordersWithRequirement = ref<JobOrderAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchOrdersWithRequirement(options: OrdersWithRequirementOptions): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getOrdersWithRequirementAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					ordersWithRequirement.value = result;
				}
			} catch (err) {
				console.error(
					'❌ ERROR useOrdersWithRequirementAutocomplete.searchOrdersWithRequirement:',
					err,
				);
				error(
					'Errore durante il caricamento delle commesse con requisiti! Ricaricare la pagina e riprovare.',
				);
				if (token.isLatest()) {
					ordersWithRequirement.value = [];
				}
			}
		}, 'searchOrdersWithRequirement');
	}

	function clearOrdersWithRequirement(): void {
		ordersWithRequirement.value = [];
	}

	return {
		ordersWithRequirement: readonly(ordersWithRequirement),
		searchOrdersWithRequirement,
		clearOrdersWithRequirement,
	};
}
